/**
 * Coach Business Builder (CBB) CTA routing for CIH V3 pages.
 *
 * Each guide/playbook carries a `ctaStageBand` (PracticeStageBand, the annual
 * revenue band). This maps that band to the matching CBB product CTA — copy +
 * destination. Starter and Earner share the Accelerator; "all" and a page with
 * no band yet send the reader to the 6-Figure Readiness Score.
 *
 * ⚠️ PLACEHOLDER HREFS — the real CBB destinations are external GoHighLevel
 * (GHL) landing pages, which are not yet provided (CIH V3 design, Open
 * Question 2). Replace the `/coach-business-builder/…` hrefs below once
 * Yuri/Thomas supply them. They are centralized here precisely so that's a
 * one-file change.
 *
 * Aliases: cbb cta, coach business builder cta, stage cta, practice stage cta
 */
import type { PracticeStageBand } from "~/lib/shared/content-status";
import { READINESS_PUBLIC_PATH } from "~/lib/shared/readiness/routes";

export type CbbCta = {
  /** Product/destination name, e.g. "CBB 9-Week Accelerator". */
  readonly label: string;
  /** One-line value proposition shown as the CTA headline. */
  readonly copy: string;
  /** Destination URL. PLACEHOLDER until GHL URLs are provided (OQ2). */
  readonly href: string;
};

const SITE_ORIGIN = "https://dreamcoachmatch.com";

// TODO(CIH-V3 OQ2): replace with real GHL destination URLs.
const PLACEHOLDER_BASE = `${SITE_ORIGIN}/coach-business-builder`;

const ACCELERATOR: CbbCta = {
  label: "CBB 9-Week Accelerator",
  copy: "Build your client acquisition system.",
  href: `${PLACEHOLDER_BASE}/accelerator`,
};

export const CBB_CTA: Record<PracticeStageBand, CbbCta> = {
  starter: ACCELERATOR,
  earner: ACCELERATOR,
  builder: {
    label: "6-Figure Coach Club",
    copy: "Get consistent. Get to $10K/mo and stay there.",
    href: `${PLACEHOLDER_BASE}/6-figure-club`,
  },
  scaler: {
    label: "7-Figure Coach Club",
    copy: "Build the systems that work without you.",
    href: `${PLACEHOLDER_BASE}/7-figure-club`,
  },
  all: {
    label: "6-Figure Readiness Score",
    copy: "Not sure where you are? Take the 2-minute readiness score.",
    href: `${SITE_ORIGIN}${READINESS_PUBLIC_PATH}`,
  },
};

/**
 * Resolve the CBB CTA for a page's CTA stage band. A page with no band yet
 * (null) or a value outside the known set gets the "all" (Readiness Score) CTA,
 * so unclassified content and a future band without a matching entry degrade
 * to a safe default instead of crashing the page render.
 */
export function getCbbCta(band: PracticeStageBand | null): CbbCta {
  return (band && CBB_CTA[band]) ?? CBB_CTA.all;
}
